import type { User } from "~/types/auth";

interface UserSession {
  MenuItems: unknown[];
  user: User;
}

export const useAuthStore = defineStore(
  "auth",
  () => {
    // State
    const session = ref<UserSession | null>(null);
    const user = ref<User | null>(null);
    const isHydrated = ref(false);
    const isLoading = ref(false);

    // Getters
    const loggedIn = computed(() => {
      return user.value !== null && isHydrated.value;
    });

    const userRoles = computed(() => {
      return user.value?.Roles || [];
    });

    const menuItems = computed(() => {
      return session.value?.MenuItems || [];
    });

    const authInfo = computed(() => ({
      loggedIn: loggedIn.value,
      userRoles: userRoles.value,
      menuItems: menuItems.value,
      session: session.value,
      user: user.value,
    }));

    // Role/Permission helpers
    const hasRole = (role: string) => {
      return userRoles.value.includes(role);
    };

    const hasAnyRole = (roles: string[]) => {
      return roles.some((role) => userRoles.value.includes(role));
    };

    const hasAllRoles = (roles: string[]) => {
      return roles.every((role) => userRoles.value.includes(role));
    };

    const verifyAuth = async () => {
      // Only verify auth on client side to prevent SSR cookie issues
      if (!import.meta.client) {
        return false;
      }

      try {
        const response = await $fetch<{ user: User | null }>("/api/verify", {
          method: "GET",
          credentials: "include",
        });

        if (response && response.user) {
          user.value = response.user as User;
          return true;
        } else {
          user.value = null;
          session.value = null;
          return false;
        }
      } catch (error) {
        user.value = null;
        session.value = null;
        return false;
      }
    };

    const awaitHydration = async () => {
      return until(isHydrated).toBeTruthy();
    };

    const getSession = async () => {
      try {
        isLoading.value = true;

        const [menuData] = await Promise.allSettled([
          $fetch("/scad/metamenus", { credentials: "include" }),
        ]);

        if (user.value) {
          session.value = {
            user: user.value,
            MenuItems:
              menuData.status === "fulfilled" ? menuData.value?.Results : [],
          };
        }

        return session.value;
      } catch (error) {
        // Don't clear session on menu fetch failure, only verify if absolutely necessary
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    const clear = async () => {
      try {
        isLoading.value = true;

        await $fetch("/api/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch (error) {
        // Logout error
      } finally {
        session.value = null;
        user.value = null;
        isLoading.value = false;
      }
    };

    const refresh = async () => {
      try {
        isLoading.value = true;

        const isValid = await verifyAuth();

        if (isValid) {
          await getSession();
        }
      } catch (error) {
        session.value = null;
        user.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const authenticate = async (
      userData: {
        UserName: string;
        Password: string;
      },
      provider: string = "credentials"
    ) => {
      try {
        isLoading.value = true;

        const res = await $fetch("/scad/api/Authenticate", {
          method: "POST",
          credentials: "include",
          body: {
            UserName: userData.UserName,
            Password: userData.Password,
            provider,
          },
        });

        if (res) {
          user.value = res;
          await getSession();
        }

        return res;
      } catch (error) {
        user.value = null;
        session.value = null;
        throw error;
      } finally {
        isLoading.value = false;
      }
    };

    const initialize = async () => {
      try {
        isLoading.value = true;

        // Only verify auth on client side to prevent SSR cookie issues
        if (import.meta.client) {
          const isValid = await verifyAuth();

          if (isValid && user.value) {
            await getSession();
          }
        }
      } catch (error) {
        user.value = null;
        session.value = null;
      } finally {
        isHydrated.value = true;
        isLoading.value = false;
      }
    };

    const syncWithServer = async () => {
      if (!import.meta.client || !isHydrated.value || isLoading.value) return;

      try {
        const isValid = await verifyAuth();

        if (isValid && user.value && !session.value) {
          await getSession();
        } else if (!isValid && (user.value || session.value)) {
          user.value = null;
          session.value = null;
        }
      } catch (error) {
        // Auth sync error
      }
    };

    const changePassword = async (oldPassword: string, newPassword: string, confirmPassword: string) => {
      const { CognitoIdentityProviderClient, ChangePasswordCommand } = await import("@aws-sdk/client-cognito-identity-provider");

      if (!user.value?.access_token) {
        throw new Error("User is not authenticated");
      }

      const runtimeConfig = useRuntimeConfig();
      const client = new CognitoIdentityProviderClient({
        region: runtimeConfig.public.NUXT_PUBLIC_THEAWSREGION
      });

      const command = new ChangePasswordCommand({
        AccessToken: user.value.access_token,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      });

      return await client.send(command);
    };

    return {
      // State
      session,
      user,
      isHydrated,
      isLoading,

      // Getters
      loggedIn,
      userRoles,
      menuItems,
      authInfo,

      // Role/Permission helpers
      hasRole,
      hasAnyRole,
      hasAllRoles,

      // Hydration helpers
      awaitHydration,

      // Actions
      clear,
      refresh,
      authenticate,
      initialize,
      syncWithServer,
      verifyAuth,
      changePassword,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
    },
  }
);

